import type { BrowserWindow, ILogger } from '@wix/thunderbolt-symbols'
import type { FreezeMetrics } from './types'

export const isMobileUserAgent = (window: BrowserWindow) => {
	if (!window?.navigator?.userAgent) {
		return false
	}
	return /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator.userAgent)
}

/**
 * Check if the device is iOS (iPhone, iPad, iPod)
 */
export const isIOSDevice = (window: BrowserWindow): boolean => {
	if (!window?.navigator?.userAgent) {
		return false
	}
	return /iPhone|iPad|iPod/i.test(window.navigator.userAgent)
}

/**
 * Check if zoom was already applied
 */
const isZoomAlreadyApplied = (window: BrowserWindow): boolean => {
	return (window as any).zoomSpxFontApplied === true
}

/**
 * Check if browser zoom handling should be applied
 * Safari 18.5+ natively supports zooming for viewport units, so we exclude it
 * Only apply for Chrome, Firefox, and mobile browsers
 * Also skip if we've already applied zoom handling via global zoom
 */
export const shouldApplyBrowserZoomHandling = (window: BrowserWindow): boolean => {
	if (!window?.navigator?.userAgent) {
		return false
	}

	// Skip if we've already applied zoom handling
	if (isZoomAlreadyApplied(window)) {
		return false
	}

	const userAgent = window.navigator.userAgent
	const isFirefox = /firefox/i.test(userAgent)
	const isMobile = /Mobile|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)
	const isChrome = userAgent.includes('Chrome')

	return isChrome || isMobile || isFirefox
}

export const detectRetinaScreen = (window: BrowserWindow) => {
	if (!window || !window.devicePixelRatio || !window.innerWidth || !window.outerWidth) {
		return 1
	}

	return Math.trunc(window.devicePixelRatio * window.innerWidth) <= window.outerWidth ? 1 : 2
}

/**
 * Detect zoom using devicePixelRatio method
 * Used for both desktop browser zoom (Ctrl/Cmd +) and Android accessibility zoom (Display Size settings)
 * Returns zoom factor (1 = no zoom)
 */
export const detectBrowserZoomForDesktop = (window: BrowserWindow): number => {
	if (!window) {
		return 1
	}

	const displayDPR = detectRetinaScreen(window)
	if (!displayDPR) {
		return 1
	}
	const dpr = window.devicePixelRatio

	// ignore zoom out
	if (dpr <= displayDPR) {
		return 1
	}

	return Math.round((dpr / displayDPR) * 100) / 100
}

const FONT_FREEZE_SELECTOR = 'body *:not(script):not(style):not(svg):not(path):not(g):not(defs):not(clipPath)'

/**
 * Query all DOM elements that support font-size styling
 */
const selectElementsToFreeze = (document: Document): NodeListOf<Element> => {
	return document.querySelectorAll(FONT_FREEZE_SELECTOR)
}

/**
 * Batch read computed styles for all elements to avoid layout thrashing
 */
const batchReadComputedStyles = (window: BrowserWindow, elements: NodeListOf<Element>): CSSStyleDeclaration[] => {
	const styles: CSSStyleDeclaration[] = []
	elements.forEach((element) => {
		styles.push(window!.getComputedStyle(element))
	})
	return styles
}

/**
 * Apply --browser-zoom CSS variable to document root
 */
const applyCssZoomVariable = (document: Document, zoomFactor: number): void => {
	document.documentElement.style.setProperty('--browser-zoom', zoomFactor.toFixed(2))
}

/**
 * Remove --browser-zoom CSS variable from document root
 */
const removeCssZoomVariable = (document: Document): void => {
	document.documentElement.style.removeProperty('--browser-zoom')
}

/**
 * Apply frozen font-size and line-height to elements
 * Returns count of elements with frozen font-size
 */
const applyFrozenStyles = (elements: NodeListOf<Element>, computedStyles: CSSStyleDeclaration[]): number => {
	let frozenCount = 0

	elements.forEach((element, i) => {
		const style = computedStyles[i]
		const fontSize = style.fontSize

		if (fontSize && fontSize !== '0px') {
			;(element as HTMLElement).style.fontSize = fontSize
			frozenCount++
		}

		const lineHeight = style.lineHeight
		if (lineHeight && lineHeight !== 'normal') {
			;(element as HTMLElement).style.lineHeight = lineHeight
		}
	})

	return frozenCount
}

/**
 * Report freeze metrics to FedOps or performance.mark()
 */
const reportFreezeMetrics = (metrics: FreezeMetrics, logger?: ILogger): void => {
	if (logger) {
		// Post-hydration: Report directly to FedOps
		logger.meter('freeze_fonts_for_browser_zoom', {
			paramsOverrides: {
				duration: String(metrics.duration),
				frozenElements: String(metrics.frozenElements),
				totalElements: String(metrics.totalElements),
				providedZoomFactor: metrics.providedZoomFactor ? String(metrics.providedZoomFactor) : '',
				devicePixelRatio: metrics.devicePixelRatio ? String(metrics.devicePixelRatio) : '',
				timestamp: String(metrics.timestamp),
				source: 'runtime',
			},
		})
	} else if (typeof performance !== 'undefined' && typeof performance.mark === 'function') {
		// Pre-hydration: Store in performance.mark() for later FedOps reporting
		performance.mark('browser-zoom-detector', {
			detail: {
				...metrics,
				source: 'browser_zoom_detector_pre_hydration',
			},
		})
	}
}

/**
 * Mark zoom font application status on window
 */
const markZoomFontApplied = (window: BrowserWindow, applied: boolean): void => {
	;(window as any).zoomSpxFontApplied = applied
}

/**
 * Handle errors during font freezing
 */
const handleFreezeError = (error: unknown, logger?: ILogger): void => {
	const errorMessage = `Failed to freeze fonts with zoom factor: ${error instanceof Error ? error.message : String(error)}`

	if (logger) {
		logger.captureError(error as Error, { tags: { feature: 'accessibility-browser-zoom' } })
	} else {
		console.error(errorMessage, error)
	}
}

/**
 * Freeze font-sizes with intelligent zoom handling
 *
 * Two scenarios:
 * 1. Pre-hydration (providedZoomFactor given): Applies --browser-zoom CSS variable temporarily
 *    to measure fonts at correct zoom level, since browser hasn't applied zoom yet.
 * 2. Runtime resize handlers (no providedZoomFactor): Reads current computed styles directly,
 *    since browser has already applied zoom.
 *
 * @param window - Browser window object
 * @param logger - Optional FedOps logger. If not provided, stores metrics in performance.mark()
 * @param providedZoomFactor - Optional pre-calculated zoom factor. When provided, triggers CSS variable approach.
 * @returns Number of elements with frozen font-size
 */
export const freezeFonts = (window: BrowserWindow, logger?: ILogger, providedZoomFactor?: number | null): number => {
	if (!window?.document) {
		return 0
	}

	const startTime = typeof performance !== 'undefined' ? performance.now() : Date.now()

	// Check if browser should apply zoom handling (exclude Safari)
	if (!shouldApplyBrowserZoomHandling(window)) {
		markZoomFontApplied(window, false)
		return 0
	}

	try {
		if (providedZoomFactor) {
			applyCssZoomVariable(window.document, providedZoomFactor)
		}

		const allElements = selectElementsToFreeze(window.document)
		const computedStyles = batchReadComputedStyles(window, allElements)

		if (providedZoomFactor) {
			removeCssZoomVariable(window.document)
		}

		const frozenCount = applyFrozenStyles(allElements, computedStyles)

		markZoomFontApplied(window, true)

		const duration = (typeof performance !== 'undefined' ? performance.now() : Date.now()) - startTime

		const visualViewport = (window as any).visualViewport
		reportFreezeMetrics(
			{
				frozenElements: frozenCount,
				totalElements: allElements.length,
				providedZoomFactor: providedZoomFactor ?? null,
				devicePixelRatio: window.devicePixelRatio ?? null,
				visualViewportScale: visualViewport?.scale ?? null,
				duration,
				timestamp: Date.now(),
			},
			logger
		)

		return frozenCount
	} catch (error) {
		handleFreezeError(error, logger)
		return 0
	}
}

/**
 * Setup mobile zoom listener
 * - iOS: Uses visualViewport.scale to detect zoom (checks scale > 1 on resize)
 * - Android: Always freezes on resize (can't reliably detect baseline DPR on devices with DPR 2.6, 3.0, etc.)
 * Note: Pinch-zoom is handled natively by CSS and doesn't need JS detection
 */
const setupMobileZoomListener = (window: BrowserWindow, logger: ILogger): (() => void) | null => {
	if (!window) {
		return null
	}

	// iOS: Use visualViewport for accurate zoom detection
	if (isIOSDevice(window)) {
		const visualViewport = (window as any).visualViewport
		if (visualViewport) {
			const scaleChangedHandler = () => {
				if (!isZoomAlreadyApplied(window) && visualViewport.scale > 1) {
					freezeFonts(window, logger)
				}
			}
			visualViewport.addEventListener('resize', scaleChangedHandler)
			return () => {
				visualViewport.removeEventListener('resize', scaleChangedHandler)
			}
		}
	}

	// Android: Always freeze on resize
	const onZoomDetectedHandler = () => {
		if (!isZoomAlreadyApplied(window)) {
			freezeFonts(window, logger)
		}
	}

	// Android: Always freeze on resize (can't reliably determine baseline DPR)
	// Many Android devices have DPR like 2.625, 3.0, 3.5, etc. which don't map to 1x or 2x baseline
	// Resize events typically indicate Display Size or accessibility settings changes
	let timeout: number | undefined
	const debouncedHandler = () => {
		if (timeout) {
			window.clearTimeout(timeout)
		}
		timeout = window.setTimeout(() => {
			onZoomDetectedHandler()
		}, 150) as any
	}

	window.addEventListener('resize', debouncedHandler)

	return () => {
		if (timeout) {
			window.clearTimeout(timeout)
		}
		window.removeEventListener('resize', debouncedHandler)
	}
}

/**
 * Setup desktop zoom listener using matchMedia for devicePixelRatio changes
 */
const setupDesktopZoomListener = (window: BrowserWindow, logger: ILogger): (() => void) | null => {
	if (!window) {
		return null
	}

	const initialDPR = window.devicePixelRatio || 1
	const mediaQuery = window.matchMedia(`(resolution: ${initialDPR}dppx)`)

	const handler = () => {
		if (isZoomAlreadyApplied(window)) {
			return
		}
		const zoomFactor = detectBrowserZoomForDesktop(window)
		if (zoomFactor > 1) {
			freezeFonts(window, logger)
		}
	}

	mediaQuery.addEventListener('change', handler, { once: true })

	return () => {
		mediaQuery.removeEventListener('change', handler)
	}
}

/**
 * Setup zoom listeners for runtime detection and freeze fonts when zoom is detected
 * Returns cleanup function to remove listeners
 */
export const setupBrowserZoomListener = (window: BrowserWindow, logger: ILogger): (() => void) | null => {
	if (!window) {
		return null
	}

	if (!shouldApplyBrowserZoomHandling(window)) {
		return null
	}

	const isMobile = isMobileUserAgent(window)
	return isMobile ? setupMobileZoomListener(window, logger) : setupDesktopZoomListener(window, logger)
}
